import {
  Badge,
  Box,
  Button,
  Checkbox,
  Field,
  HStack,
  IconButton,
  Input,
  NativeSelect,
  NumberInput,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { SegmentedControl, toasterApi } from '@ui';
import React from 'react';
import { LuPlus, LuSearch, LuTrash2, LuX } from 'react-icons/lu';
import { ProjectsApi, ReleasesApi, TrackingApi } from '@/state/api';
import { useAppSelector } from '@/state';
import { TrackingType } from '@/types';
import { newRequestId } from '../utils';

const SOURCE_TYPES: {
  value: TrackingType.SourceRow['source_type'];
  label: string;
  needsRelease?: boolean;
}[] = [
  { value: 'frame', label: 'Frames (units)', needsRelease: true },
  { value: 'crate', label: 'Crates' },
  { value: 'shipment', label: 'Shipments (manifest)' },
  { value: 'dispatch_line', label: 'Site delivery lines' },
  { value: 'inventory_item', label: 'Inventory items (lots)' },
  { value: 'part', label: 'Parts (lots)' },
  { value: 'fabrication_work_order', label: 'Fabrication work orders (lots)' },
];

interface ItemsPanelProps {
  selections: TrackingType.ComposerSelection[];
  onAdd: (items: TrackingType.ComposerSelection[]) => void;
  onChangeCopies: (code: string, copies: number) => void;
  onRemove: (code: string) => void;
  pendingSources: TrackingType.ComposerSource[];
  onResolvePending: (
    source: TrackingType.ComposerSource,
    items: TrackingType.ComposerSelection[],
  ) => void;
  onDropPending: (source: TrackingType.ComposerSource) => void;
  maxCopies: number;
}

const CANONICAL = new Set(['crate', 'shipment', 'dispatch_line']);

const ExistingLots: React.FC<{
  source: TrackingType.ComposerSource;
  onPick: (item: TrackingType.ComposerSelection) => void;
}> = ({ source, onPick }) => {
  const { data } = TrackingApi.Api.useGetItemsQuery({
    source_type: source.source_type,
    source_id: source.source_id,
    state: 'ACTIVE',
    limit: 25,
  });
  if (!data?.items.length) return null;
  return (
    <NativeSelect.Root size="xs" width="180px">
      <NativeSelect.Field
        aria-label="Existing lot"
        value=""
        onChange={(e) => {
          const item = data.items.find((i) => i.code === e.target.value);
          if (item)
            onPick({
              code: item.code,
              display_code: item.display_code,
              title: item.title,
              copies: 1,
              state: item.state,
            });
        }}
      >
        <option value="">Existing lot…</option>
        {data.items.map((i) => (
          <option key={i.code} value={i.code}>
            {i.display_code}
            {i.lot_quantity ? ` · ${i.lot_quantity} ${i.lot_unit}` : ''}
          </option>
        ))}
      </NativeSelect.Field>
      <NativeSelect.Indicator />
    </NativeSelect.Root>
  );
};

const toSelection = (item: {
  code: string;
  display_code: string;
  title: string;
  state?: TrackingType.ItemState;
}) => ({
  code: item.code,
  display_code: item.display_code,
  title: item.title,
  copies: 1,
  state: item.state,
});

const UnitPicker: React.FC<{
  frameId: number;
  initial?: number[];
  onDone: (items: TrackingType.ComposerSelection[]) => void;
  onCancel: () => void;
}> = ({ frameId, initial, onDone, onCancel }) => {
  const { data, isLoading } = TrackingApi.Api.useGetFrameUnitsQuery({
    frame_id: frameId,
  });
  const [register, { isLoading: registering }] =
    TrackingApi.Api.usePostRegisterMutation();
  const planned = (data?.units ?? []).filter((u) => u.slot_index);
  const [selected, setSelected] = React.useState<Set<number>>(
    new Set(initial ?? []),
  );
  const [range, setRange] = React.useState('');
  React.useEffect(() => {
    if (!initial?.length && planned.length && selected.size === 0)
      setSelected(new Set());
  }, [planned.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyRange = () => {
    const next = new Set(selected);
    range
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        const [a, b] = part.split('-').map((n) => Number(n));
        const end = Number.isFinite(b) ? b : a;
        for (let i = Math.min(a, end); i <= Math.max(a, end); i += 1)
          if (i >= 1 && i <= (data?.frame.quantity ?? 0)) next.add(i);
      });
    setSelected(next);
  };

  const confirm = async () => {
    try {
      const res = await register({
        source_type: 'frame',
        source_id: frameId,
        unit_indexes: Array.from(selected),
        request_id: newRequestId(),
      }).unwrap();
      onDone(res.items.map(toSelection));
    } catch {
      toasterApi({
        type: 'error',
        status: 400,
        code: 'Units',
        message: 'The frame units could not be registered',
      });
    }
  };

  if (isLoading || !data)
    return <Text textStyle="body.small">Loading units…</Text>;
  return (
    <Stack gap={2} borderWidth="1px" borderRadius="md" p={3} bg="bg.panel">
      <HStack justify="space-between" flexWrap="wrap">
        <Text fontWeight="semibold">
          {data.frame.name} · Release {data.frame.release_number} ·{' '}
          {data.frame.quantity} planned
        </Text>
        <Text textStyle="body.small" color="fg.muted">
          Each unit has its own permanent code.
        </Text>
      </HStack>
      {data.reconciliation && (
        <Text textStyle="body.small" color="orange.fg">
          The planned quantity changed. Slots{' '}
          {data.reconciliation.open_slots.join(', ')} need a decision before
          they can be labelled.
        </Text>
      )}
      <HStack gap={2} flexWrap="wrap">
        <Button
          size="xs"
          variant="outline"
          onClick={() =>
            setSelected(new Set(planned.map((u) => u.slot_index!)))
          }
        >
          All units
        </Button>
        <Button
          size="xs"
          variant="outline"
          onClick={() => setSelected(new Set())}
        >
          None
        </Button>
        <Input
          size="xs"
          maxW="200px"
          placeholder="e.g. 1-5, 8"
          value={range}
          onChange={(e) => setRange(e.target.value)}
          aria-label="Unit range"
        />
        <Button size="xs" variant="outline" onClick={applyRange}>
          Add range
        </Button>
      </HStack>
      <Box maxH="220px" overflowY="auto">
        <Stack gap={1}>
          {planned.length === 0 && (
            <Text textStyle="body.small" color="fg.muted">
              No units are registered yet. Confirming registers{' '}
              {data.frame.quantity} units for this frame.
            </Text>
          )}
          {planned.map((u) => (
            <Checkbox.Root
              key={u.unit_id}
              checked={selected.has(u.slot_index!)}
              onCheckedChange={(e) => {
                const next = new Set(selected);
                if (e.checked) next.add(u.slot_index!);
                else next.delete(u.slot_index!);
                setSelected(next);
              }}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
              <Checkbox.Label>
                Unit {u.slot_index} of {data.frame.quantity}
                {u.identity && (
                  <Text as="span" color="fg.muted" ml={2}>
                    {u.identity.display_code}
                  </Text>
                )}
              </Checkbox.Label>
            </Checkbox.Root>
          ))}
        </Stack>
      </Box>
      <HStack justify="flex-end">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          colorPalette="blue"
          onClick={confirm}
          loading={registering}
          disabled={planned.length > 0 && selected.size === 0}
        >
          Add {selected.size || (planned.length ? 0 : data.frame.quantity)} unit
          {selected.size === 1 ? '' : 's'}
        </Button>
      </HStack>
    </Stack>
  );
};

const LotForm: React.FC<{
  source: TrackingType.SourceRow;
  onDone: (item: TrackingType.ComposerSelection) => void;
  onCancel: () => void;
}> = ({ source, onDone, onCancel }) => {
  const [createItem, { isLoading }] = TrackingApi.Api.usePostItemMutation();
  const [qty, setQty] = React.useState('');
  const [unit, setUnit] = React.useState('pcs');
  const [title, setTitle] = React.useState(source.label);
  const key = React.useRef(newRequestId());
  const submit = async () => {
    try {
      const item = await createItem({
        source_type: source.source_type,
        source_id: source.source_id,
        title,
        lot_quantity: qty || null,
        lot_unit: qty ? unit : '',
        creation_key: key.current,
      }).unwrap();
      onDone(toSelection(item));
    } catch {
      /* field errors are reported by the global handler */
    }
  };
  return (
    <Stack gap={2} borderWidth="1px" borderRadius="md" p={3} bg="bg.panel">
      <Text fontWeight="semibold">New lot of {source.label}</Text>
      <Text textStyle="body.small" color="fg.muted">
        Creates a separate tracked lot. It does not change stock. Each new lot
        gets its own code.
      </Text>
      <Field.Root>
        <Field.Label>Title</Field.Label>
        <Input
          size="sm"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field.Root>
      <HStack>
        <Field.Root>
          <Field.Label>Lot quantity (optional)</Field.Label>
          <Input
            size="sm"
            inputMode="decimal"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </Field.Root>
        <Field.Root>
          <Field.Label>Unit</Field.Label>
          <Input
            size="sm"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
          />
        </Field.Root>
      </HStack>
      <HStack justify="flex-end">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          colorPalette="blue"
          onClick={submit}
          loading={isLoading}
          disabled={!title.trim()}
        >
          Create lot
        </Button>
      </HStack>
    </Stack>
  );
};

export const StandaloneForm: React.FC<{
  onDone: (item: TrackingType.ItemDetail) => void;
  onCancel: () => void;
  defaultProjectId?: number | null;
}> = ({ onDone, onCancel, defaultProjectId }) => {
  const { data: projects } = ProjectsApi.Api.useGetAllProjectsQuery();
  const permissions = useAppSelector(
    (s) => s.auth.current.user?.permissions ?? [],
  ) as string[];
  const canShop = permissions.includes('access_shop_trackeditem');
  const [createItem, { isLoading }] = TrackingApi.Api.usePostItemMutation();
  const [scope, setScope] = React.useState<'PROJECT' | 'SHOP'>('PROJECT');
  const [projectId, setProjectId] = React.useState<string>(
    defaultProjectId ? String(defaultProjectId) : '',
  );
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [granularity, setGranularity] =
    React.useState<TrackingType.Granularity>('UNIT');
  const [qty, setQty] = React.useState('');
  const [unit, setUnit] = React.useState('');
  const [meta, setMeta] = React.useState<TrackingType.MetadataField[]>([]);
  const key = React.useRef(newRequestId());
  const submit = async () => {
    try {
      const item = await createItem({
        source_type: 'standalone',
        scope,
        project_id: scope === 'PROJECT' ? Number(projectId) : null,
        title,
        description,
        granularity,
        lot_quantity: qty || null,
        lot_unit: qty ? unit : '',
        metadata: { fields: meta.filter((m) => m.label.trim()) },
        creation_key: key.current,
      }).unwrap();
      onDone(item);
    } catch {
      /* reported by the global handler */
    }
  };
  return (
    <Stack gap={3} borderWidth="1px" borderRadius="md" p={3} bg="bg.panel">
      <Text fontWeight="semibold">Register a standalone item</Text>
      <Text textStyle="body.small" color="fg.muted">
        For anything leaving the shop that has no ERP record yet. It can be
        linked to an ERP record later.
      </Text>
      <HStack gap={3} flexWrap="wrap">
        <Field.Root maxW="160px">
          <Field.Label>Scope</Field.Label>
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              value={scope}
              onChange={(e) => setScope(e.target.value as 'PROJECT' | 'SHOP')}
            >
              <option value="PROJECT">Project</option>
              {canShop && <option value="SHOP">Shop</option>}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Field.Root>
        {scope === 'PROJECT' && (
          <Field.Root flex={1} minW="200px" required>
            <Field.Label>Project</Field.Label>
            <NativeSelect.Root size="sm">
              <NativeSelect.Field
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">Choose a project</option>
                {(projects ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </Field.Root>
        )}
        <Field.Root maxW="170px">
          <Field.Label>Tracked as</Field.Label>
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              value={granularity}
              onChange={(e) =>
                setGranularity(e.target.value as TrackingType.Granularity)
              }
            >
              <option value="UNIT">Single unit</option>
              <option value="LOT">Lot / box</option>
              <option value="CONTAINER">Container</option>
              <option value="DOCUMENT">Document</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </Field.Root>
      </HStack>
      <Field.Root required>
        <Field.Label>Title</Field.Label>
        <Input
          size="sm"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Spare gasket box for Tower A"
        />
      </Field.Root>
      <Field.Root>
        <Field.Label>Description</Field.Label>
        <Textarea
          size="sm"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field.Root>
      {granularity === 'LOT' && (
        <HStack>
          <Field.Root>
            <Field.Label>Lot quantity</Field.Label>
            <Input
              size="sm"
              inputMode="decimal"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
            />
          </Field.Root>
          <Field.Root>
            <Field.Label>Unit</Field.Label>
            <Input
              size="sm"
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              placeholder="pcs"
            />
          </Field.Root>
        </HStack>
      )}
      <Stack gap={1}>
        <Text textStyle="fieldLabel">Details</Text>
        {meta.map((m, i) => (
          <HStack key={i}>
            <Input
              size="sm"
              placeholder="Label"
              value={m.label}
              aria-label={`Detail ${i + 1} label`}
              onChange={(e) =>
                setMeta(
                  meta.map((x, j) =>
                    j === i ? { ...x, label: e.target.value } : x,
                  ),
                )
              }
            />
            <Input
              size="sm"
              placeholder="Value"
              value={m.value}
              aria-label={`Detail ${i + 1} value`}
              onChange={(e) =>
                setMeta(
                  meta.map((x, j) =>
                    j === i ? { ...x, value: e.target.value } : x,
                  ),
                )
              }
            />
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Remove detail"
              onClick={() => setMeta(meta.filter((_, j) => j !== i))}
            >
              <LuX />
            </IconButton>
          </HStack>
        ))}
        <Box>
          <Button
            size="xs"
            variant="outline"
            onClick={() => setMeta([...meta, { label: '', value: '' }])}
          >
            <LuPlus /> Add detail
          </Button>
        </Box>
      </Stack>
      <HStack justify="flex-end">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          colorPalette="blue"
          onClick={submit}
          loading={isLoading}
          disabled={!title.trim() || (scope === 'PROJECT' && !projectId)}
        >
          Register item
        </Button>
      </HStack>
    </Stack>
  );
};

export const ItemsPanel: React.FC<ItemsPanelProps> = ({
  selections,
  onAdd,
  onChangeCopies,
  onRemove,
  pendingSources,
  onResolvePending,
  onDropPending,
  maxCopies,
}) => {
  const selectedProjectId = useAppSelector((s) => s.project.selectedProject.id);
  const { data: projects } = ProjectsApi.Api.useGetAllProjectsQuery();
  const [sourceType, setSourceType] =
    React.useState<TrackingType.SourceRow['source_type']>('frame');
  const [projectId, setProjectId] = React.useState<string>(
    selectedProjectId ? String(selectedProjectId) : '',
  );
  const [releaseId, setReleaseId] = React.useState<string>('');
  const [search, setSearch] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [itemSearch, setItemSearch] = React.useState('');
  const [itemQuery, setItemQuery] = React.useState('');
  const [unitFrame, setUnitFrame] = React.useState<{
    id: number;
    initial?: number[];
    pending?: TrackingType.ComposerSource;
  } | null>(null);
  const [lotSource, setLotSource] =
    React.useState<TrackingType.SourceRow | null>(null);
  const [standalone, setStandalone] = React.useState(false);
  const [addPath, setAddPath] = React.useState<'erp' | 'existing' | 'new'>(
    'erp',
  );
  const [register] = TrackingApi.Api.usePostRegisterMutation();
  const needsRelease = SOURCE_TYPES.find(
    (t) => t.value === sourceType,
  )?.needsRelease;
  const { data: releases } = ReleasesApi.Api.useGetAllReleaseQuery(
    projectId ? { project: Number(projectId) } : undefined,
    { skip: !projectId },
  );
  const sourcesArgs = {
    source_type: sourceType,
    project_id: projectId || undefined,
    release_id: releaseId || undefined,
    search: query || undefined,
    limit: 25,
  };
  const {
    data: sources,
    isFetching,
    isError: sourcesError,
    refetch: refetchSources,
  } = TrackingApi.Api.useGetSourcesQuery(
    sourcesArgs,
    {
      skip:
        !projectId &&
        sourceType !== 'inventory_item' &&
        sourceType !== 'dispatch_line',
    },
  );
  const { data: found, isFetching: findingItems } =
    TrackingApi.Api.useGetItemsQuery(
      { search: itemQuery, limit: 10 },
      { skip: !itemQuery },
    );
  const totalLabels = selections.reduce((n, s) => n + s.copies, 0);

  React.useEffect(() => {
    const pendingFrame = pendingSources.find((s) => s.source_type === 'frame');
    if (pendingFrame && !unitFrame)
      setUnitFrame({
        id: pendingFrame.source_id,
        initial: pendingFrame.unit_indexes,
        pending: pendingFrame,
      });
  }, [pendingSources, unitFrame]);

  const addCanonical = async (row: TrackingType.SourceRow) => {
    try {
      const res = await register({
        source_type: row.source_type,
        source_id: row.source_id,
        request_id: newRequestId(),
      }).unwrap();
      onAdd(res.items.map(toSelection));
    } catch {
      /* reported globally */
    }
  };

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <HStack justify="space-between">
          <Text as="h2" textStyle="heading.form" tabIndex={-1}>
            What would you like to label?
          </Text>
          <Badge variant="subtle">
            {selections.length} item{selections.length === 1 ? '' : 's'} ·{' '}
            {totalLabels} label{totalLabels === 1 ? '' : 's'}
          </Badge>
        </HStack>
        {pendingSources.filter((s) => s.source_type !== 'frame').length > 1 && (
          <Box>
            <Button
              size="xs"
              variant="outline"
              onClick={async () => {
                for (const s of pendingSources.filter((p) =>
                  CANONICAL.has(p.source_type),
                )) {
                  try {
                    const res = await register({
                      source_type: s.source_type,
                      source_id: s.source_id,
                      request_id: newRequestId(),
                    }).unwrap();
                    onResolvePending(s, res.items.map(toSelection));
                  } catch {
                    /* reported globally */
                  }
                }
              }}
            >
              Register and add all records
            </Button>
          </Box>
        )}
        {pendingSources
          .filter((s) => s.source_type !== 'frame')
          .map((s) => (
            <HStack
              key={`${s.source_type}-${s.source_id}`}
              borderWidth="1px"
              borderStyle="dashed"
              borderRadius="md"
              p={2}
              justify="space-between"
            >
              <Text textStyle="body.small">
                {s.label ?? `${s.source_type} #${s.source_id}`} ·{' '}
                {CANONICAL.has(s.source_type)
                  ? 'not registered yet'
                  : 'choose a lot'}
              </Text>
              <HStack>
                {CANONICAL.has(s.source_type) ? (
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const res = await register({
                          source_type: s.source_type,
                          source_id: s.source_id,
                          request_id: newRequestId(),
                        }).unwrap();
                        onResolvePending(s, res.items.map(toSelection));
                      } catch {
                        /* reported globally */
                      }
                    }}
                  >
                    Register &amp; add
                  </Button>
                ) : (
                  <>
                    <ExistingLots
                      source={s}
                      onPick={(item) => onResolvePending(s, [item])}
                    />
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => {
                        setLotSource({
                          source_type:
                            s.source_type as TrackingType.SourceRow['source_type'],
                          source_id: s.source_id,
                          label: s.label ?? '',
                          sublabel: '',
                          project_id: null,
                          project_name: null,
                          canonical: false,
                          supports_units: false,
                          pending: s,
                        } as TrackingType.SourceRow & {
                          pending: TrackingType.ComposerSource;
                        });
                      }}
                    >
                      New lot
                    </Button>
                  </>
                )}
                <IconButton
                  size="xs"
                  variant="ghost"
                  aria-label="Remove"
                  onClick={() => onDropPending(s)}
                >
                  <LuX />
                </IconButton>
              </HStack>
            </HStack>
          ))}
        {selections.length === 0 && pendingSources.length === 0 && (
          <Text textStyle="body.small" color="fg.muted">
            Start here. Choose an existing tracked item, an ERP record, or
            register a new standalone item. You will customize the label next,
            then review and print. The print is saved as a job on that item.
          </Text>
        )}
        <Stack gap={1} maxH="260px" overflowY="auto">
          {selections.map((s) => (
            <HStack
              key={s.code}
              borderWidth="1px"
              borderRadius="md"
              px={2}
              py={1.5}
              gap={2}
            >
              <Box flex={1} minW={0}>
                <Text fontSize="sm" truncate>
                  {s.title}
                </Text>
                <Text fontSize="xs" color="fg.muted">
                  {s.display_code}
                  {s.state && s.state !== 'ACTIVE' ? ` · ${s.state}` : ''}
                </Text>
              </Box>
              <NumberInput.Root
                size="xs"
                width="90px"
                min={1}
                max={maxCopies}
                value={String(s.copies)}
                onValueChange={(e) =>
                  onChangeCopies(
                    s.code,
                    Math.max(1, Math.min(maxCopies, Number(e.value) || 1)),
                  )
                }
              >
                <NumberInput.Label srOnly>
                  Copies of {s.display_code}
                </NumberInput.Label>
                <NumberInput.Control />
                <NumberInput.Input />
              </NumberInput.Root>
              <Text
                fontSize="xs"
                color="fg.muted"
                display={{ base: 'none', md: 'block' }}
              >
                copies
              </Text>
              <IconButton
                size="xs"
                variant="ghost"
                aria-label={`Remove ${s.display_code}`}
                onClick={() => onRemove(s.code)}
              >
                <LuTrash2 />
              </IconButton>
            </HStack>
          ))}
        </Stack>
        <Text textStyle="body.small" color="fg.muted">
          Copies use the same code. Separate physical units have separate codes.
        </Text>
      </Stack>

      {unitFrame && (
        <UnitPicker
          frameId={unitFrame.id}
          initial={unitFrame.initial}
          onDone={(items) => {
            onAdd(items);
            if (unitFrame.pending) onResolvePending(unitFrame.pending, []);
            setUnitFrame(null);
          }}
          onCancel={() => {
            if (unitFrame.pending) onDropPending(unitFrame.pending);
            setUnitFrame(null);
          }}
        />
      )}
      {lotSource && (
        <LotForm
          source={lotSource}
          onDone={(item) => {
            const pending = (
              lotSource as { pending?: TrackingType.ComposerSource }
            ).pending;
            if (pending) onResolvePending(pending, [item]);
            else onAdd([item]);
            setLotSource(null);
          }}
          onCancel={() => setLotSource(null)}
        />
      )}
      {standalone && (
        <StandaloneForm
          defaultProjectId={selectedProjectId}
          onCancel={() => setStandalone(false)}
          onDone={(item) => {
            onAdd([toSelection(item)]);
            setStandalone(false);
          }}
        />
      )}

      <Stack gap={3} borderTopWidth="1px" pt={3}>
        <Text textStyle="fieldLabel.bold" id="add-items-label">
          Add items
        </Text>
        <SegmentedControl
          size="sm"
          aria-labelledby="add-items-label"
          value={addPath}
          items={[
            { value: 'erp', label: 'From ERP' },
            { value: 'existing', label: 'Existing item' },
            { value: 'new', label: 'New standalone' },
          ]}
          onValueChange={(e: { value: string | null }) => {
            if (e.value === 'erp' || e.value === 'existing' || e.value === 'new')
              setAddPath(e.value);
            if (e.value !== 'new') setStandalone(false);
          }}
        />
      {addPath === 'erp' && (
      <Stack gap={2}>
        <HStack gap={2} flexWrap="wrap">
          <NativeSelect.Root size="sm" width={{ base: '100%', md: '230px' }}>
            <NativeSelect.Field
              aria-label="Record type"
              value={sourceType}
              onChange={(e) => {
                setSourceType(
                  e.target.value as TrackingType.SourceRow['source_type'],
                );
                setReleaseId('');
              }}
            >
              {SOURCE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          <NativeSelect.Root size="sm" width={{ base: '100%', md: '220px' }}>
            <NativeSelect.Field
              aria-label="Project"
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                setReleaseId('');
              }}
            >
              <option value="">
                {sourceType === 'inventory_item' ||
                sourceType === 'dispatch_line'
                  ? 'All my projects'
                  : 'Choose a project'}
              </option>
              {(projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
          {(needsRelease || sourceType === 'fabrication_work_order') && (
            <NativeSelect.Root
              size="sm"
              width={{ base: '100%', md: '160px' }}
              disabled={!projectId}
            >
              <NativeSelect.Field
                aria-label="Release"
                value={releaseId}
                onChange={(e) => setReleaseId(e.target.value)}
              >
                <option value="">All releases</option>
                {(releases ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.number}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(search.trim());
            }}
            style={{ display: 'flex', gap: 8, flex: 1, minWidth: 200 }}
          >
            <Input
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or number"
              aria-label="Search records"
            />
            <IconButton
              size="sm"
              type="submit"
              aria-label="Search records"
              variant="outline"
            >
              <LuSearch />
            </IconButton>
          </form>
        </HStack>
        <Stack gap={1} maxH="260px" overflowY="auto" aria-busy={isFetching}>
          {(sources?.items ?? []).map((row) => (
            <HStack
              key={`${row.source_type}-${row.source_id}`}
              borderWidth="1px"
              borderRadius="md"
              px={2}
              py={1.5}
              justify="space-between"
              gap={2}
            >
              <Box minW={0}>
                <Text fontSize="sm" truncate>
                  {row.label}
                </Text>
                <Text fontSize="xs" color="fg.muted" truncate>
                  {[
                    row.project_name,
                    row.sublabel,
                    row.identity ? `Code ${row.identity.display_code}` : null,
                    row.lot_count ? `${row.lot_count} lot(s)` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </Box>
              {row.supports_units ? (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => setUnitFrame({ id: row.source_id })}
                >
                  Choose units
                </Button>
              ) : row.canonical ? (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => addCanonical(row)}
                  disabled={row.identity?.state === 'VOID'}
                >
                  {row.identity ? 'Add' : 'Register & add'}
                </Button>
              ) : (
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => setLotSource(row)}
                >
                  New lot
                </Button>
              )}
            </HStack>
          ))}
          {sourcesError && (
            <Text textStyle="body.small" color="fg.muted">
              Records could not be loaded.{' '}
              <Button size="xs" variant="ghost" onClick={() => refetchSources()}>
                Retry
              </Button>
            </Text>
          )}
          {sources && sources.items.length === 0 && !sourcesError && (
            <Text textStyle="body.small" color="fg.muted">
              No matching records.
            </Text>
          )}
          {!projectId &&
            sourceType !== 'inventory_item' &&
            sourceType !== 'dispatch_line' && (
              <Text textStyle="body.small" color="fg.muted">
                Choose a project to list records.
              </Text>
            )}
        </Stack>
      </Stack>
      )}

      {addPath === 'existing' && (
      <Stack gap={2}>
        <Text textStyle="fieldLabel.bold">Existing tracked item</Text>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setItemQuery(itemSearch.trim());
          }}
          style={{ display: 'flex', gap: 8 }}
        >
          <Input
            size="sm"
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            placeholder="Code, numeric code or title"
            aria-label="Code, numeric code or title"
          />
          <IconButton
            size="sm"
            type="submit"
            aria-label="Search tracked items"
            variant="outline"
            loading={findingItems}
          >
            <LuSearch />
          </IconButton>
        </form>
        {(found?.items ?? []).map((item) => (
          <HStack
            key={item.code}
            borderWidth="1px"
            borderRadius="md"
            px={2}
            py={1.5}
            justify="space-between"
          >
            <Box minW={0}>
              <Text fontSize="sm" truncate>
                {item.title}
              </Text>
              <Text fontSize="xs" color="fg.muted">
                {item.display_code} · {item.kind_label} · {item.state_label}
              </Text>
            </Box>
            <Button
              size="xs"
              variant="outline"
              onClick={() => onAdd([toSelection(item)])}
              disabled={item.state !== 'ACTIVE'}
            >
              Add
            </Button>
          </HStack>
        ))}
        {found && found.items.length === 0 && (
          <Text textStyle="body.small" color="fg.muted">
            No tracked items match.
          </Text>
        )}
      </Stack>
      )}

      {addPath === 'new' && (
        <Stack gap={2}>
          {!standalone ? (
            <Button size="sm" variant="outline" onClick={() => setStandalone(true)}>
              <LuPlus /> Register standalone item
            </Button>
          ) : (
            <Text textStyle="body.small" color="fg.muted">
              Fill in the standalone item above. It is added to this list when
              you register it.
            </Text>
          )}
        </Stack>
      )}
      </Stack>
    </Stack>
  );
};
